import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApi, api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import UserAvatar from '../components/UserAvatar';
import useMobile from '../hooks/useMobile';

const REACTION_EMOJIS = ['❤️','👍','😂','😮','😢','🎉','🔥','👏','💯','🙈'];
const QUICK_EMOJIS    = ['❤️','😂','👍','🎉','🔥','😮','😢','👏'];

function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString('nb-NO',{hour:'2-digit',minute:'2-digit'});
}
function fmtDate(ts) {
  const d = new Date(ts), t = new Date();
  if (d.toDateString()===t.toDateString()) return 'I dag';
  const y = new Date(t); y.setDate(t.getDate()-1);
  if (d.toDateString()===y.toDateString()) return 'I går';
  return d.toLocaleDateString('nb-NO',{weekday:'long',day:'numeric',month:'long'});
}

function groupReactions(list=[]) {
  return list.reduce((m,r)=>{
    (m[r.emoji]=m[r.emoji]||[]).push(r); return m;
  },{});
}

function groupByDate(msgs) {
  const out=[]; let last=null;
  for (const m of msgs) {
    const d=new Date(m.created_at).toDateString();
    if (d!==last){out.push({type:'date',label:fmtDate(m.created_at),key:'d'+d});last=d;}
    out.push({type:'msg',...m});
  }
  return out;
}

export default function ChatPage() {
  const isMobile = useMobile();
  const { user, family } = useAuth();
  const { dark } = useTheme();
  const [msgs, setMsgs]           = useState([]);
  const [hasMore, setHasMore]     = useState(false);
  const [text, setText]           = useState('');
  const [replyTo, setReplyTo]     = useState(null);
  const [selected, setSelected]   = useState(null); // id of tapped message
  const [showReact, setShowReact] = useState(null); // id for reaction picker
  const [showEmoji, setShowEmoji] = useState(false);
  const [search, setSearch]       = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  const { data: pinned, refresh: refreshPinned } = useApi('/api/messages/pinned');

  const load = useCallback(async (replace=true, before=null) => {
    const url = `/api/messages?limit=50${before?`&before=${before}`:''}`;
    const data = await api.get(url).catch(()=>[]);
    if (replace) { setMsgs(data); setHasMore(data.length>=50); }
    else { setMsgs(p=>[...data,...p]); setHasMore(data.length>=50); }
  }, []);

  useEffect(()=>{ load(); },[]);
  useEffect(()=>{ const t=setInterval(()=>load(),5000); return()=>clearInterval(t); },[load]);
  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:'smooth'}); },[msgs.length]);

  // Lukk meny ved klikk utenfor
  useEffect(()=>{
    const h=()=>{ setSelected(null); setShowReact(null); };
    document.addEventListener('click',h);
    return()=>document.removeEventListener('click',h);
  },[]);

  const send = async e => {
    e.preventDefault();
    if (!text.trim()) return;
    await api.post('/api/messages',{content:text,reply_to_id:replyTo?.id||null});
    setText(''); setReplyTo(null); setShowEmoji(false);
    load();
  };

  const react = async (id, emoji) => {
    setShowReact(null); setSelected(null);
    await api.post(`/api/messages/${id}/react`,{emoji});
    load();
  };

  const del = async id => {
    setSelected(null);
    if (!window.confirm('Slett meldingen?')) return;
    await api.delete(`/api/messages/${id}`);
    setMsgs(m=>m.filter(x=>x.id!==id));
  };

  const pin = async id => {
    setSelected(null);
    await api.put(`/api/messages/${id}/pin`,{});
    load(); refreshPinned();
  };

  const doSearch = async () => {
    if (!search.trim()) { setSearchResults(null); return; }
    const data = await api.get(`/api/messages/search?q=${encodeURIComponent(search)}`);
    setSearchResults(data);
  };

  const selectMsg = (e, id) => {
    e.stopPropagation();
    setSelected(s => s===id ? null : id);
    setShowReact(null);
  };

  const displayed = searchResults ?? msgs;
  const grouped   = groupByDate(displayed);
  const isAdmin   = user?.role==='admin';

  const pageBg  = dark ? '#0A0818' : '#EDE8FF';
  const txt      = dark ? '#E8E8F8' : '#1A1A35';
  const sub2     = dark ? '#9090B8' : '#888888';
  const border   = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';
  const inputBg  = dark ? 'rgba(28,26,52,0.98)' : 'rgba(255,255,255,0.95)';

  if (isMobile) return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:pageBg, fontFamily:"'Plus Jakarta Sans',sans-serif" }}
      onClick={()=>{setSelected(null);setShowReact(null);}}>

      {/* Gradient Hero Header */}
      <div style={{
        background: dark
          ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
          : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
        padding: '52px 20px 18px',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
            <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Familie-chat</div>
            {family?.members_count && <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', marginTop:2 }}>{family.members_count} medlemmer</div>}
          </div>
          <button onClick={()=>setSearch(s=>s?'':' ')} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 12px', cursor:'pointer', fontSize:18, color:'#fff' }}>🔍</button>
        </div>
        {search !== '' && (
          <div style={{ display:'flex', gap:8, marginTop:12, position:'relative', zIndex:1 }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doSearch()}
              placeholder="Søk i meldinger..." autoFocus
              style={{ flex:1, padding:'9px 14px', borderRadius:12, border:'1px solid rgba(255,255,255,0.25)', background:'rgba(255,255,255,0.15)', color:'#fff', fontSize:14, fontFamily:'inherit', outline:'none' }}/>
            <button onClick={doSearch} style={{ background:'rgba(255,255,255,0.2)', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', borderRadius:12, padding:'0 14px', fontWeight:700, cursor:'pointer' }}>Søk</button>
            <button onClick={()=>{setSearch('');setSearchResults(null);}} style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:12, padding:'0 12px', cursor:'pointer', color:'rgba(255,255,255,0.7)' }}>✕</button>
          </div>
        )}
      </div>

      {/* Festet melding */}
      {(pinned||[]).length>0 && (
        <div style={{ background:dark?'rgba(255,200,0,0.08)':'#FFF8F0', borderBottom:'1px solid '+(dark?'rgba(255,200,0,0.15)':'#FFE4C4'), padding:'8px 16px', display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          <span>📌</span>
          <span style={{ flex:1, fontSize:13, color:dark?'#D4A017':'#92400E', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pinned[0].content}</span>
        </div>
      )}

      {/* Meldingsliste */}
      <div style={{ flex:1, overflowY:'auto', padding:'12px 12px 8px' }}>
        {hasMore && !searchResults && (
          <div style={{ textAlign:'center', marginBottom:10 }}>
            <button onClick={()=>load(false,msgs[0]?.created_at)} style={{ background:dark?'rgba(255,255,255,0.08)':'rgba(124,111,247,0.1)', border:'none', borderRadius:20, padding:'6px 16px', fontSize:12, color:sub2, cursor:'pointer' }}>
              ↑ Eldre meldinger
            </button>
          </div>
        )}
        {displayed.length===0 && (
          <div style={{ textAlign:'center', padding:'40px 20px', color:sub2, fontSize:15 }}>
            {searchResults!=null ? '🔍 Ingen treff' : '💬 Send den første meldingen!'}
          </div>
        )}
        {grouped.map(item => {
          if (item.type==='date') return (
            <div key={item.key} style={{ textAlign:'center', margin:'10px 0' }}>
              <span style={{ background:'rgba(124,111,247,0.12)', borderRadius:20, padding:'4px 14px', fontSize:11, color:sub2, fontWeight:600 }}>{item.label}</span>
            </div>
          );
          const isMe = item.user_id===user?.id;
          const reactions = groupReactions(item.reactions);
          return (
            <div key={item.id} style={{ display:'flex', flexDirection: isMe?'row-reverse':'row', alignItems:'flex-end', gap:8, marginBottom:8 }}>
              {!isMe && <UserAvatar photo={item.user_photo} emoji={item.avatar} name={item.user_name} color={item.color} size={28}/>}
              <div style={{ maxWidth:'75%' }}>
                {!isMe && <div style={{ fontSize:11, fontWeight:700, color:sub2, marginBottom:2, paddingLeft:4 }}>{item.user_name}</div>}
                {item.reply_to_content && (
                  <div style={{ fontSize:11, color:sub2, background:dark?'rgba(255,255,255,0.07)':'rgba(0,0,0,.06)', borderLeft:'3px solid #7C6FF7', padding:'4px 8px', borderRadius:'8px 8px 0 0', marginBottom:-4 }}>
                    {item.reply_to_content.slice(0,60)}
                  </div>
                )}
                <div
                  onClick={e=>{e.stopPropagation();selectMsg(e,item.id);}}
                  style={{
                    background: isMe ? 'linear-gradient(135deg,#7C6FF7,#9358E8)' : (dark?'rgba(255,255,255,0.07)':'rgba(255,255,255,0.9)'),
                    color: isMe ? '#fff' : txt,
                    borderRadius: isMe?'18px 4px 18px 18px':'4px 18px 18px 18px',
                    padding:'10px 14px', fontSize:14, lineHeight:1.5,
                    boxShadow:'0 1px 8px rgba(0,0,0,.1)',
                    cursor:'pointer', wordBreak:'break-word',
                    border: isMe ? 'none' : '1px solid '+border,
                  }}>
                  {item.content}
                  <span style={{ display:'block', fontSize:10, opacity:.7, marginTop:3, textAlign:'right' }}>{fmtTime(item.created_at)}</span>
                </div>
                {Object.keys(reactions).length>0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:4 }}>
                    {Object.entries(reactions).map(([emoji,list])=>(
                      <button key={emoji} onClick={e=>{e.stopPropagation();react(item.id,emoji);}}
                        style={{ background:dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.9)', border:'1px solid '+border, borderRadius:20, padding:'2px 8px', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:3 }}>
                        {emoji} <span style={{fontSize:11, color:sub2}}>{list.length}</span>
                      </button>
                    ))}
                  </div>
                )}
                {selected===item.id && (
                  <div style={{ display:'flex', gap:6, marginTop:6, flexWrap:'wrap' }}>
                    {QUICK_EMOJIS.map(e=>(
                      <button key={e} onClick={ev=>{ev.stopPropagation();react(item.id,e);}}
                        style={{ background:dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.9)', border:'1px solid '+border, borderRadius:20, width:34, height:34, fontSize:16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {e}
                      </button>
                    ))}
                    <button onClick={e=>{e.stopPropagation();setReplyTo({id:item.id,content:item.content});setSelected(null);}}
                      style={{ background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:20, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}>↩ Svar</button>
                    {(isMe||isAdmin) && (
                      <button onClick={e=>{e.stopPropagation();del(item.id);}}
                        style={{ background:'#FEE2E2', color:'#E5534B', border:'none', borderRadius:20, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}>🗑</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div style={{ background:inputBg, backdropFilter:'blur(20px)', borderTop:'1px solid '+border, padding:'10px 12px', flexShrink:0 }}>
        {replyTo && (
          <div style={{ background:dark?'rgba(255,255,255,0.07)':'rgba(124,111,247,0.08)', borderRadius:10, padding:'6px 12px', marginBottom:8, display:'flex', alignItems:'center', gap:8, borderLeft:'3px solid #7C6FF7' }}>
            <div style={{ flex:1, fontSize:12, color:sub2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>↩ {replyTo.content}</div>
            <button onClick={()=>setReplyTo(null)} style={{ background:'none', border:'none', color:sub2, cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
        )}
        <form onSubmit={send} style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
          <textarea
            value={text}
            onChange={e=>{setText(e.target.value);e.target.style.height='auto';e.target.style.height=Math.min(e.target.scrollHeight,120)+'px';}}
            onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();send(e);}}}
            placeholder="Skriv melding..."
            rows={1}
            style={{ flex:1, padding:'11px 14px', borderRadius:22, border:'1px solid '+border, background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none', resize:'none', lineHeight:1.5, maxHeight:120 }}
          />
          <button type="submit" style={{ background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:'50%', width:44, height:44, fontSize:20, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>↑</button>
        </form>
      </div>
    </div>
  );

  return (
    <div style={s.wrap} onClick={()=>{setSelected(null);setShowReact(null);}}>

      {/* Festet melding */}
      {(pinned||[]).length>0 && (
        <div style={s.pinBanner}>
          <span style={s.pinIcon}>📌</span>
          <span style={s.pinText}>{pinned[0].content.slice(0,70)}{pinned[0].content.length>70?'…':''}</span>
          {pinned.length>1 && <span style={s.pinMore}>+{pinned.length-1}</span>}
        </div>
      )}

      {/* Meldingsliste */}
      <div style={s.list}>
        {hasMore && !searchResults && (
          <button style={s.loadMore}
            onClick={()=>load(false, msgs[0]?.created_at)}>
            ↑ Eldre meldinger
          </button>
        )}

        {displayed.length===0 && (
          <div style={s.empty}>
            {searchResults!=null ? '🔍 Ingen treff' : '💬 Send den første meldingen til familien!'}
          </div>
        )}

        {grouped.map(item=>{
          if (item.type==='date') return (
            <div key={item.key} style={s.dateDivider}>
              <span style={s.dateLabel}>{item.label}</span>
            </div>
          );

          const isMe = item.user_id===user?.id;
          const reactions = groupReactions(item.reactions);
          const isSelected = selected===item.id;
          const canDel = isMe||isAdmin;

          return (
            <div key={item.id} style={{...s.row, ...(isMe?s.rowMe:{})}}>

              {/* Avatar */}
              {!isMe && (
                <UserAvatar photo={item.user_photo} emoji={item.avatar} name={item.user_name} color={item.color} size={32} />
              )}

              <div style={s.col}>
                {!isMe && <div style={s.name}>{item.user_name}</div>}

                {/* Sitat */}
                {item.reply_to_id && item.reply_content && (
                  <div style={{...s.quote, ...(isMe?s.quoteMe:{})}}>
                    <span style={s.quoteName}>{item.reply_user_name}</span>
                    <span style={s.quoteText}>{item.reply_content.slice(0,60)}{item.reply_content.length>60?'…':''}</span>
                  </div>
                )}

                {/* Boble */}
                <div style={{...s.bubble, ...(isMe?s.bubbleMe:s.bubbleThem), ...(item.pinned?s.pinned:{})}}
                  onClick={e=>selectMsg(e,item.id)}>
                  {item.content}
                </div>

                {/* Handlingsmeny (vises ved klikk) */}
                {isSelected && (
                  <div style={{...s.menu, ...(isMe?s.menuMe:{})}} onClick={e=>e.stopPropagation()}>
                    <button style={s.menuBtn} onClick={()=>{setShowReact(item.id);setSelected(null);}}>😊 Reaksjon</button>
                    <button style={s.menuBtn} onClick={()=>{setReplyTo(item);setSelected(null);inputRef.current?.focus();}}>↩️ Svar</button>
                    {isAdmin && <button style={s.menuBtn} onClick={()=>pin(item.id)}>{item.pinned?'📌 Fjern fest':'📍 Fest'}</button>}
                    {canDel && <button style={{...s.menuBtn,...s.menuDel}} onClick={()=>del(item.id)}>🗑 Slett</button>}
                  </div>
                )}

                {/* Emoji-velger */}
                {showReact===item.id && (
                  <div style={{...s.reactPicker, ...(isMe?s.reactPickerMe:{})}} onClick={e=>e.stopPropagation()}>
                    {REACTION_EMOJIS.map(e=>(
                      <button key={e} style={s.reactBtn} onClick={()=>react(item.id,e)}>{e}</button>
                    ))}
                  </div>
                )}

                {/* Reaksjoner */}
                {Object.keys(reactions).length>0 && (
                  <div style={{...s.reactRow, ...(isMe?{justifyContent:'flex-end'}:{})}}>
                    {Object.entries(reactions).map(([emoji,users])=>(
                      <button key={emoji}
                        style={{...s.reactBadge,...(users.some(u=>u.user_id===user?.id)?s.reactBadgeMe:{})}}
                        title={users.map(u=>u.user_name).join(', ')}
                        onClick={e=>{e.stopPropagation();react(item.id,emoji);}}>
                        {emoji}{users.length>1&&<span style={s.reactN}>{users.length}</span>}
                      </button>
                    ))}
                  </div>
                )}

                <div style={{...s.time, textAlign:isMe?'right':'left'}}>
                  {fmtTime(item.created_at)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Svar-forhåndsvisning */}
      {replyTo && (
        <div style={s.replyBar}>
          <div style={s.replyInfo}>
            <span style={s.replyLabel}>↩️ Svar til <strong>{replyTo.user_name}</strong></span>
            <span style={s.replyContent}>{replyTo.content.slice(0,50)}{replyTo.content.length>50?'…':''}</span>
          </div>
          <button style={s.replyX} onClick={()=>setReplyTo(null)}>✕</button>
        </div>
      )}

      {/* Hurtig-emoji */}
      {showEmoji && (
        <div style={s.emojiRow}>
          {QUICK_EMOJIS.map(e=>(
            <button key={e} style={s.emojiBtn}
              onClick={()=>{setText(t=>t+e);inputRef.current?.focus();}}>
              {e}
            </button>
          ))}
        </div>
      )}

      {/* Søk */}
      {search!==null && (
        <div style={s.inputRow}>
          {/* Søkevisning */}
          {searchResults!==null && (
            <div style={s.searchActive}>
              🔍 {searchResults.length} treff
              <button style={s.searchClear} onClick={()=>{setSearch('');setSearchResults(null);}}>✕ Lukk</button>
            </div>
          )}
        </div>
      )}

      {/* Input */}
      <form onSubmit={send} style={s.form}>
        <button type="button" style={s.toolBtn}
          onClick={e=>{e.stopPropagation();setShowEmoji(v=>!v);}}>
          {showEmoji?'✕':'😊'}
        </button>
        <input ref={inputRef} style={s.input} value={text}
          placeholder={replyTo?`Svar til ${replyTo.user_name}…`:'Skriv en melding…'}
          onChange={e=>setText(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&send(e)}
          autoComplete="off"
        />
        <button type="button" style={s.toolBtn}
          onClick={e=>{e.stopPropagation();const v=!showEmoji;setShowEmoji(false);
            const q=window.prompt('Søk i meldinger:');
            if(q){setSearch(q);api.get(`/api/messages/search?q=${encodeURIComponent(q)}`).then(d=>setSearchResults(d));}
          }}>
          🔍
        </button>
        <button type="submit" style={{...s.sendBtn,opacity:text.trim()?1:.45}} disabled={!text.trim()}>
          ➤
        </button>
      </form>
    </div>
  );
}

const s = {
  wrap:          { display:'flex', flexDirection:'column', height:'calc(100vh - 120px)' },
  pinBanner:     { display:'flex', alignItems:'center', gap:8, background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:10, padding:'7px 12px', marginBottom:6, flexShrink:0, fontSize:13 },
  pinIcon:       { flexShrink:0 },
  pinText:       { flex:1, color:'var(--yellow)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  pinMore:       { background:'#FCD34D', borderRadius:20, padding:'1px 7px', fontSize:11, fontWeight:700, color:'#78350F', flexShrink:0 },
  list:          { flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:4, paddingBottom:4 },
  loadMore:      { alignSelf:'center', padding:'5px 16px', borderRadius:20, border:'1px solid var(--bdr)', background:'var(--card)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', margin:'4px auto' },
  empty:         { textAlign:'center', padding:60, color:'var(--txl)', fontSize:14, lineHeight:2 },
  dateDivider:   { textAlign:'center', margin:'8px 0', flexShrink:0 },
  dateLabel:     { fontSize:11, fontWeight:700, color:'var(--txl)', background:'var(--bg)', padding:'3px 12px', borderRadius:20 },
  row:           { display:'flex', gap:8, alignItems:'flex-end', paddingRight:4 },
  rowMe:         { flexDirection:'row-reverse', paddingRight:0, paddingLeft:4 },
  avatar:        { width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, color:'#fff', flexShrink:0, marginBottom:18 },
  col:           { display:'flex', flexDirection:'column', maxWidth:'72%' },
  name:          { fontSize:11, fontWeight:700, color:'var(--txl)', marginBottom:2, paddingLeft:4 },
  quote:         { background:'var(--bdr)', borderLeft:'3px solid #7C6FF7', borderRadius:'8px 8px 0 0', padding:'4px 10px', marginBottom:-8, display:'flex', flexDirection:'column' },
  quoteMe:       { borderLeft:'none', borderRight:'3px solid #7C6FF7', alignItems:'flex-end' },
  quoteName:     { fontSize:10, fontWeight:700, color:'#7C6FF7' },
  quoteText:     { fontSize:11, color:'var(--txm)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  bubble:        { padding:'9px 14px', borderRadius:18, fontSize:14, lineHeight:1.5, wordBreak:'break-word', cursor:'pointer', userSelect:'none' },
  bubbleThem:    { background:'var(--card)', color:'var(--txt)', border:'1px solid var(--bdr)', borderBottomLeftRadius:4 },
  bubbleMe:      { background:'#7C6FF7', color:'#fff', borderBottomRightRadius:4 },
  pinned:        { outline:'2px solid #FCD34D', outlineOffset:1 },
  menu:          { display:'flex', flexWrap:'wrap', gap:4, background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:12, padding:'6px 8px', marginTop:4, boxShadow:'0 4px 16px rgba(0,0,0,.1)', zIndex:20, alignSelf:'flex-start' },
  menuMe:        { alignSelf:'flex-end' },
  menuBtn:       { padding:'5px 10px', borderRadius:8, border:'none', background:'var(--bg)', color:'var(--txt)', fontWeight:600, cursor:'pointer', fontSize:12, fontFamily:'inherit', whiteSpace:'nowrap' },
  menuDel:       { background:'#FFF5F5', color:'var(--red)' },
  reactPicker:   { display:'flex', gap:3, background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:24, padding:'6px 10px', boxShadow:'0 4px 16px rgba(0,0,0,.1)', flexWrap:'wrap', maxWidth:240, marginTop:4, alignSelf:'flex-start', zIndex:20 },
  reactPickerMe: { alignSelf:'flex-end' },
  reactBtn:      { background:'none', border:'none', cursor:'pointer', fontSize:20, padding:'1px 2px', borderRadius:6, lineHeight:1 },
  reactRow:      { display:'flex', flexWrap:'wrap', gap:3, marginTop:2 },
  reactBadge:    { display:'flex', alignItems:'center', gap:1, padding:'2px 7px', borderRadius:20, border:'1px solid var(--bdr)', background:'var(--bg)', cursor:'pointer', fontSize:13, fontFamily:'inherit', lineHeight:1.4 },
  reactBadgeMe:  { background:'var(--bdr)', borderColor:'#7C6FF7' },
  reactN:        { fontSize:10, fontWeight:700, color:'var(--txm)', marginLeft:1 },
  time:          { fontSize:10, color:'var(--txl)', marginTop:2, paddingLeft:3, paddingRight:3 },
  replyBar:      { display:'flex', alignItems:'center', background:'var(--bg)', borderRadius:10, padding:'7px 12px', marginTop:4, gap:10, border:'1px solid var(--bdr)', flexShrink:0 },
  replyInfo:     { flex:1, display:'flex', flexDirection:'column', gap:1, minWidth:0 },
  replyLabel:    { fontSize:12, color:'#7C6FF7' },
  replyContent:  { fontSize:12, color:'var(--txm)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  replyX:        { background:'none', border:'none', cursor:'pointer', color:'var(--txl)', fontSize:14, flexShrink:0, padding:4 },
  emojiRow:      { display:'flex', gap:4, padding:'4px 2px', flexShrink:0 },
  emojiBtn:      { fontSize:22, background:'none', border:'none', cursor:'pointer', padding:'2px 3px', borderRadius:8, lineHeight:1 },
  searchActive:  { display:'flex', alignItems:'center', gap:10, fontSize:13, color:'var(--txm)', padding:'4px 0' },
  searchClear:   { padding:'4px 10px', borderRadius:8, border:'1px solid var(--bdr)', background:'var(--card)', color:'#F7605C', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit' },
  inputRow:      { flexShrink:0 },
  form:          { display:'flex', gap:6, paddingTop:8, borderTop:'1px solid var(--bdr)', alignItems:'center', flexShrink:0 },
  toolBtn:       { width:38, height:38, borderRadius:'50%', border:'1px solid var(--bdr)', background:'var(--card)', cursor:'pointer', fontSize:17, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontFamily:'inherit' },
  input:         { flex:1, padding:'10px 16px', borderRadius:22, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:14, outline:'none', background:'var(--input-bg)', color:'var(--input-txt)' },
  sendBtn:       { width:40, height:40, borderRadius:'50%', border:'none', background:'#7C6FF7', color:'#fff', fontSize:17, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
};
